// The "?" that opens Help & session (spec §6).
//
//   <SessionHelpButton inline />  inside a screen header (Game.jsx's question
//                                 header). While any inline button is mounted
//                                 the provider hides its floating one.
//   floating (the provider's own) fixed to the top-right corner of every other
//                                 screen.
//
// It renders nothing when the help layer is hidden: no seat, the TV, a phone
// still hydrating the game, or room_help_sheet off for this session.
import { useEffect } from 'react';
import SessionIcon from './icons';
import { liteMotion } from '../../lib/anim-tokens';
import { useSessionHelp } from './context';

// 30px from the top on phones without a notch, and 12px below the safe area
// on phones with one.
export const FLOATING_STYLE = {
  top: 'max(calc(env(safe-area-inset-top) + 12px), 30px)',
};

/** True when a vote or a readmit request is waiting on this viewer. */
export function needsAttention(ctx) {
  if (!ctx) return false;
  const vote = ctx.openVote;
  const voteWaiting = Boolean(vote && vote.can_vote && vote.my_ballot == null);
  const now = Date.now();
  const readmitWaiting = ctx.isGm && (ctx.readmits || []).some((r) => Date.parse(r.expires_at) > now);
  return voteWaiting || readmitWaiting;
}

export default function SessionHelpButton({ inline = false, floating = false, onCurtain = false, className = '' }) {
  const ctx = useSessionHelp();
  const registerInline = ctx?.registerInline;

  useEffect(() => {
    if (!inline || !registerInline) return undefined;
    return registerInline();
  }, [inline, registerInline]);

  if (!ctx?.visible) return null;
  // On a curtain the screen's own header button is covered, so this one shows
  // whatever the screen underneath registered (Mel, 2026-09-28).
  if (floating && !onCurtain && !ctx.showFloating) return null;

  const dot = needsAttention(ctx);
  const pulse = dot && !liteMotion(); // decorative: static dot under lite motion

  const button = (
    <button
      type="button"
      onClick={() => ctx.openHelp('home')}
      aria-label={dot ? 'Help — something needs you' : 'Help'}
      aria-haspopup="dialog"
      data-testid={floating ? 'session-help-floating' : 'session-help-inline'}
      className={`relative flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.10)] ${className}`}
    >
      <SessionIcon name="help" className="h-7 w-7" />
      {dot && (
        <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3" aria-hidden="true">
          {pulse && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tasted-burg/50" />}
          <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-white bg-tasted-burg" />
        </span>
      )}
    </button>
  );

  if (!floating) return button;
  return (
    <div className={`${onCurtain ? 'absolute' : 'fixed'} right-4 z-[60]`} style={FLOATING_STYLE}>
      {button}
    </div>
  );
}
